import { Assert } from "../Assert";
import { PartialNullable, RadPromiseLike } from "../Extensions";
import { ArrayHelper } from "../Helpers/ArrayHelper";
import { CookieHelper } from "../Helpers/CookieHelper";
import { ObjectHelper } from "../Helpers/ObjectHelper";
import { IPromisePublic, PromiseHelper } from "../Helpers/PromiseHelper";
import { StringHelper } from "../Helpers/StringHelper";
import { $$, IJNativeEvent, JNativeStatic } from "../JNative";
import { LinqArray } from "../LinqArray";
import { PropertyChange } from "../PropertyChange";
import { Property, PropertyObject, PropertyString, RadObject } from "../RadObject";
import { ApplicationInformationService } from "../RadWindow";
import { ServiceManager } from "../ServiceManager";
import { ApplicationDataService } from "../Ui/ApplicationDataService";
import { Button } from "../Ui/Controls/Button";
import { Dialog, DialogPropertyValues, IDialog } from "../Ui/Controls/Dialog";
import { ErrorDialog } from "../Ui/Controls/ErrorDialog";
import { MessageDialog } from "../Ui/Controls/MessageDialog";
import { PageDimmer } from "../Ui/Controls/PageDimmer";
import { DialogResultEnum } from "../Ui/Dialogs/DialogResultEnum";
import { AjaxError } from "./AjaxError";
import { AjaxRequestPropertyValues } from "./AjaxRequestPropertyValues";
import { IAjaxRequest } from "./IAjaxRequest";
import { IWebClient } from "./IWebClient";
import { ServerErrorHelper } from "./ServerErrorHelper";
import { NAMESPACE } from "./_Namespace";

import "./AjaxRequest.scss";

export type TenantSelectDialogPropertyValues =
    DialogPropertyValues
    & {
    };

export type TenantSelectDialogProperties = {
    [name in keyof TenantSelectDialogPropertyValues]: Property<TenantSelectDialogPropertyValues[name]>
};

export interface ITenantSelectDialog extends IDialog, TenantSelectDialogProperties
{
}

export class TenantSelectDialog extends Dialog implements ITenantSelectDialog
{
    // #region Static

    public static override classNamespace = NAMESPACE + "AjaxRequest";
    public static override className = "TenantSelectDialog";

    // #region Constructor

    static {

        $$(document).on("click", ".rad-control-dialog-tenant-select .rad-control-button", (evt: IJNativeEvent) =>
        {
            const
                $$currentTarget = $$(evt.currentTarget),
                button = $$currentTarget.radControl() as Button,
                tenantId = button?.tag.get()?.id,
                dialog = button?.page.get() as Dialog;

            if (tenantId && dialog)
            {
                const
                    additionalHeaders: any = {},
                    authenticationData = ServiceManager.getOptional(ApplicationDataService)?.authenticationData.get();

                if (authenticationData)
                {
                    switch (authenticationData.scheme)
                    {
                        case "Bearer":
                            if (authenticationData.accessToken)
                                additionalHeaders["Authorization"] = `${authenticationData.scheme} ${authenticationData.accessToken}`;
                            break;
                        case "Cookies":
                            if (authenticationData.antiForgeryToken)
                                additionalHeaders["X-XSRF-Token"] = authenticationData.antiForgeryToken;
                            break;
                    }
                }

                JNativeStatic
                    .ajax({
                        type: "POST",
                        // TODO: BAM
                        //headers: ObjectHelper.extend(true, {}, WebApiClient.staticHeaders, additionalHeaders),
                        headers: ObjectHelper.extend(true, {}, additionalHeaders),
                        dataType: "json",
                        contentType: "application/json",
                        url: StringHelper.format("{0}api/Security/{1}", ServiceManager.getRequired(ApplicationInformationService).serverVirtualDirectory, "SetTenant"),
                        data: ObjectHelper.toJson({ tenantId: tenantId }),
                        xhrFields: {
                            withCredentials: true
                        }
                    })
                    .then(result =>
                    {
                        dialog.dialogResult = DialogResultEnum.ok;
                        dialog.promiseData = result.response;

                        return dialog.closeAsync();
                    })
                    .catch(error =>
                    {
                        ErrorDialog.displayError(error);
                    })
            }
        });

    }

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<TenantSelectDialogPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                title: "Select an account",
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    private tenants?: { id: string, name: string }[];

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TenantSelectDialogPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-dialog-tenant-select")
    }

    // #endregion

    // #region Overrides

    protected override _activateAsync()
    {
        this.controlsClear();

        this.append(
            LinqArray.create(this.tenants)
                .orderBy(item => item.name)
                .select(item => new Button({ tag: item, text: item.name }))
        );

        return super._activateAsync();
    }
    protected override _createElementComplete()
    {
        super._createElementComplete();

        this._pageFooterControlRef.current?.hide();
    }
    protected override _dispose()
    {
        this.dimmer?.remove();
        super._dispose();
    }
    protected override _getDimmer()
    {
        if (!this.dimmer)
            this.dimmer = new PageDimmer({ animationIn: this.animationIn.get(), animationOut: this.animationOut.get(), cssClass: "rad-dialog-dimmer rad-dialog-dimmer-tenant-select" });

        return this.dimmer;
    }

    public override openAsync(data?: any)
    {
        this.tenants = data || [];
        return super.openAsync(data);
    }

    // #endregion

    // #endregion
}

interface IODataResponse
{
    "@odata.count": number;
    value: { total: number };
}

interface IAjaxRequestCall
{
    webClient: IWebClient;
    settings: any;
    promise: IPromisePublic<any>;
    dontShowDialogForErrorsOfType?: string[];
}

export class AjaxRequest extends RadObject implements IAjaxRequest
{
    // #region Static

    public static override classNamespace = NAMESPACE + "AjaxRequest";
    public static override className = "AjaxRequest";

    // #region Global Variables

    public static callQueue: IAjaxRequestCall[] = [];
    public static disconnected = false;
    public static disconnectedDialog?: MessageDialog;
    public static tenantSelectDialog?: TenantSelectDialog;
    public static triggerConnectionEvent = true;
    public static unauthenticated = false;

    // #endregion

    static {

        $$(window).on("online", () =>
        {
            AjaxRequest.disconnected = false;
            AjaxRequest.processCallQueue();
        });

        $$(window).on("offline", () =>
        {
            AjaxRequest.disconnected = true;
            AjaxRequest.triggerConnectionEvent = true;
        });

    }

    // #region Public Methods

    public static disconnectionDetected(webClient: IWebClient, xhr: XMLHttpRequest)
    {
        if (AjaxRequest.disconnected)
            return;

        AjaxRequest.disconnected = true;
        AjaxRequest.triggerConnectionEvent = true;

        const
            onDisconnected = webClient.onDisconnected.get();

        if (onDisconnected)
            onDisconnected();

        if (!AjaxRequest.disconnectedDialog)
            AjaxRequest.disconnectedDialog = new MessageDialog();

        if (xhr.status === 503)
        {
            AjaxRequest.disconnectedDialog?.title.set("Server Unavailable");
            AjaxRequest.disconnectedDialog.text.set("The server is currently unavailable.");

            AjaxRequest.disconnectedDialog
                .openAsync()
                .then(() =>
                {
                    AjaxRequest.disconnected = false;
                    AjaxRequest.processCallQueue();
                });
        }
        else if (!webClient.inhibitNetworkErrors.get())
        {
            AjaxRequest.disconnectedDialog.title.set("Network Error");
            AjaxRequest.disconnectedDialog.text.set("A network connection issue has been detected. Please ensure you have an internet connection and click \"OK\" to try again.");

            AjaxRequest.disconnectedDialog
                .openAsync()
                .then(() =>
                {
                    AjaxRequest.disconnected = false;
                    AjaxRequest.processCallQueue();
                });
        }
    }
    public static processCallQueue(antiForgeryToken?: string)
    {
        const
            callQueueClone = AjaxRequest.callQueue;

        AjaxRequest.callQueue = [];

        while (callQueueClone.length > 0)
        {
            const
                call = callQueueClone.shift();

            if (!call)
                continue;

            if (antiForgeryToken && call.settings.headers)
                call.settings.headers["X-XSRF-Token"] = antiForgeryToken;

            AjaxRequest._ajax(call.webClient, call.settings, call.promise, call.dontShowDialogForErrorsOfType);
        }
    }

    // #endregion

    // #region Protected Methods

    protected static _ajax<T>(webClient: IWebClient, settings: any, promise?: IPromisePublic<T>, dontShowDialogForErrorsOfType?: string[]): IPromisePublic<T>
    {
        const
            internalPromise = promise ? promise : PromiseHelper.createPromisePublic<T>(),
            internalSettings = ObjectHelper.extend(true, {
                showLoading: true,
                xhrFields: {
                    withCredentials: true
                }
            }, settings);

        if (!internalSettings.headers)
            internalSettings.headers = {};

        if (AjaxRequest.disconnected || AjaxRequest.unauthenticated)
            AjaxRequest.callQueue.push({ webClient: webClient, settings: internalSettings, promise: internalPromise });
        else
            AjaxRequest._ajaxInternal(webClient, internalSettings, internalPromise, dontShowDialogForErrorsOfType);

        return internalPromise;
    }
    protected static _ajaxInternal<T>(webClient: IWebClient, options: any, promise: IPromisePublic<T>, dontShowDialogForErrorsOfType?: string[])
    {
        const
            onRequestAuthenticationData = webClient.onRequestAuthenticationData.get(),
            authenticationData = onRequestAuthenticationData ? onRequestAuthenticationData() : null;

        if (!authenticationData || !(authenticationData.scheme === "Cookies"))
            CookieHelper.delete("__RequestVerificationToken");

        if (authenticationData)
        {
            switch (authenticationData.scheme)
            {
                case "Bearer":
                    {
                        if (authenticationData.accessToken)
                            options.headers["Authorization"] = `${authenticationData.scheme} ${authenticationData.accessToken}`;
                        break;
                    }
            }
        }

        JNativeStatic
            .ajax(options)
            .then(results =>
            {
                const
                    hasResponseMessages = results.xhr.getAllResponseHeaders().indexOf("RAD-Response-Messages") >= 0,
                    responseMessagesBase64 = hasResponseMessages ? results.xhr.getResponseHeader("RAD-Response-Messages") : null;

                if (responseMessagesBase64)
                {
                    const
                        responseMessages = ObjectHelper.fromJson(window.atob(responseMessagesBase64));

                    if (responseMessages?.length)
                    {
                        $$(document).triggerHandler("SeriouslyRAD.AjaxRequest:ResponseMessagesReceived", {
                            responseMessages: responseMessages
                        });
                    }
                }

                return results;
            })
            .then(results =>
            {
                if (AjaxRequest.triggerConnectionEvent)
                {
                    AjaxRequest.triggerConnectionEvent = false;

                    const
                        onConnected = webClient.onConnected.get();

                    if (onConnected)
                        onConnected();
                }

                promise.resolve(results.response);
            })
            .catch((xhr: XMLHttpRequest) =>
            {
                if (xhr.readyState === 0 || xhr.status <= 0 || xhr.status === 503 || !navigator.onLine)
                {
                    AjaxRequest.callQueue.push({ webClient: webClient, settings: options, promise: promise, dontShowDialogForErrorsOfType: dontShowDialogForErrorsOfType });
                    AjaxRequest.disconnectionDetected(webClient, xhr);
                }
                else
                    AjaxRequest._ajaxCallFailed(webClient, ServerErrorHelper.processError(xhr), options, promise, dontShowDialogForErrorsOfType);
            });
    }
    protected static _ajaxCallFailed<T>(webClient: IWebClient, error: AjaxError, settings: any, promise: IPromisePublic<T>, dontShowDialogForErrorsOfType?: string[])
    {
        switch (error.type.get())
        {
            case "AuthenticationApiException":
            case "UnauthorizedApiException":
                {
                    if (!settings.dontQueueFailedAuthenticationCall)
                        AjaxRequest.callQueue.push({ webClient: webClient, settings: settings, promise: promise, dontShowDialogForErrorsOfType: dontShowDialogForErrorsOfType });

                    AjaxRequest.unauthenticated = true;

                    if ((webClient as any).onUnauthenticated?.get)
                    {
                        const
                            onUnauthenticated = (webClient as any).onUnauthenticated.get();

                        if (onUnauthenticated)
                            onUnauthenticated();
                    }

                    if (settings.dontQueueFailedAuthenticationCall)
                        promise.reject(error);

                    break;
                }
            case "MfaAuthenticationApiException":
            case "ValidationApiException":
                promise.reject(error);
                break;
            case "TenantAuthenticationApiException":
                {
                    AjaxRequest.callQueue.push({ webClient: webClient, settings: settings, promise: promise, dontShowDialogForErrorsOfType: dontShowDialogForErrorsOfType });

                    if (!AjaxRequest.tenantSelectDialog)
                        AjaxRequest.tenantSelectDialog = new TenantSelectDialog();

                    AjaxRequest.tenantSelectDialog
                        .openAsync(error.additionalData.get())
                        .then(result =>
                        {
                            if (result.dialogResult === DialogResultEnum.ok)
                                AjaxRequest.processCallQueue(result.promiseData.antiForgeryToken);
                        });

                    break;
                }
            default:
                {
                    const
                        errorType = error.type.get();

                    if (settings.displayExceptions)
                    if (!ArrayHelper.isArrayLike(dontShowDialogForErrorsOfType) || (errorType && dontShowDialogForErrorsOfType.indexOf(errorType) === -1))
                        ErrorDialog.displayError(error);

                    promise.reject(error);
                    break;
                }
        }
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    public dontShowDialogForErrorsOfType!: Property<string[]>;
    public settings!: PropertyObject;
    public type!: PropertyString;
    public webClient!: Property<IWebClient>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<AjaxRequestPropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Public Methods

    public executeAsync<TReturn extends any = any>(options?: any): RadPromiseLike<TReturn>
    {
        const
            internalSettings = ObjectHelper.extend(true, {}, options, this.settings.get());

        return (
            this._ajax(internalSettings)
                .then((data: any) =>
                {
                    return new Promise((resolve, reject) =>
                    {
                        switch (this.type.get())
                        {
                            case "API":
                                resolve(data);
                                break;
                            case "ODATA":
                                if (!data)
                                    resolve(undefined as any);
                                else if (this._isValidODataResponse(data))
                                {
                                    const
                                        oDataResponse = data as IODataResponse;

                                    if (oDataResponse["@odata.count"] !== undefined)
                                    {
                                        oDataResponse.value.total = oDataResponse["@odata.count"];
                                        resolve(oDataResponse.value as any);
                                    }
                                    else
                                        resolve(data);
                                }
                                else
                                    reject(AjaxError.fromErrorMessage("Invalid data returned from the server"));
                                break;
                            default:
                                resolve(data);
                                break;
                        }
                    });
                })
        );
    }

    // #endregion

    // #region Protected Methods

    protected _ajax<T>(settings: any, promise?: IPromisePublic<T>): IPromisePublic<T>
    {
        if (!settings.headers)
            settings.headers = {};

        const
            webClient = this.webClient.get();

        Assert.isNotNullOrUndefined(webClient, "webClient");

        return AjaxRequest._ajax<T>(webClient, settings, promise, this.dontShowDialogForErrorsOfType.get() ?? undefined);
    }
    protected _ajaxCallFailed<T>(error: AjaxError, settings: any, promise: IPromisePublic<T>)
    {
        const
            webClient = this.webClient.get();

        Assert.isNotNullOrUndefined(webClient, "webClient");

        return AjaxRequest._ajaxCallFailed(webClient, error, settings, promise, this.dontShowDialogForErrorsOfType.get() ?? undefined);
    }
    protected _dontShowDialogForErrorsOfTypeChanged(change: PropertyChange<string[]>)
    {
        /* Intentionally empty */
    }
    protected _isValidODataResponse(data: any): boolean
    {
        return data !== undefined && data !== null && data["@odata.context"] !== undefined;
    }
    protected _settingsChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected _typeChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _webClientChanged(change: PropertyChange<IWebClient>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: AjaxRequestPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.dontShowDialogForErrorsOfType = new Property<string[]>(this, "System.Object, System.Private.CoreLib", propertyValues.dontShowDialogForErrorsOfType, this._dontShowDialogForErrorsOfTypeChanged);
        this.settings = new PropertyObject(this, propertyValues.settings, this._settingsChanged);
        this.type = new PropertyString(this, propertyValues.type, this._typeChanged);
        this.webClient = new Property<IWebClient>(this, "System.Object, System.Private.CoreLib", propertyValues.webClient, this._webClientChanged);
    }

    // #endregion

    // #endregion
}
